import { Injectable } from '@angular/core';
import { append, deleteProperty, removeNodeByGid } from '@models/apollo';
import {
  CancelIlBookingMutation,
  CreateIlBookingForUserInput,
  CreateIlBookingForUserMutation,
  CreateIlBookingInput,
  CreateIlBookingMutation,
  CreatePendingIlBookingReservationInput,
  CreatePendingIlBookingReservationMutation,
  ForcePromoteIlBookingMutation,
  IlBooking,
  UpdateIlClassResultsInput,
  UpdateMultipleIlClassResultsMutation
} from '@queries/generated';
import { castAs, finalMap } from '@rx/final-map';
import { gidToApolloCacheKey } from '@util/gid-to-apollo-cache-key';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class ILBookingService {
  constructor(
    private createIlBookingMutation: CreateIlBookingMutation,
    private cancelIlBookingMutation: CancelIlBookingMutation,
    private createIlBookingForUserMutation: CreateIlBookingForUserMutation,
    private forcePromoteIlBookingMutation: ForcePromoteIlBookingMutation,
    private updateMultipleIlClassResultsMutation: UpdateMultipleIlClassResultsMutation,
    private createPendingReservationMutation: CreatePendingIlBookingReservationMutation
  ) {}

  create(input: CreateIlBookingInput) {
    return this.createIlBookingMutation.mutate({ input }).pipe(
      map(({ data }) => data.createIlBooking),
      finalMap('ilBooking'),
      castAs<IlBooking>()
    );
  }

  cancel(ilBooking: IlBooking) {
    return this.cancelIlBookingMutation
      .mutate(
        { input: { ilBookingId: ilBooking.gid } },
        {
          update: (store, { data: { cancelIlBooking } }) => {
            let id = gidToApolloCacheKey(cancelIlBooking.ilBooking.courseItemProgression.gid);
            store.modify({
              id,
              fields: {
                ilBooking: deleteProperty()
              }
            });

            // Make sure to remove from the cached list of bookings for an IL Class
            id = gidToApolloCacheKey(cancelIlBooking.ilBooking.ilClass.gid);
            store.modify({
              id,
              fields: {
                ilBookings: removeNodeByGid(cancelIlBooking.ilBooking.gid)
              }
            });
          }
        }
      )
      .pipe(
        map(({ data }) => data.cancelIlBooking),
        finalMap('ilBooking')
      );
  }

  promoteBooking(ilBookingId: string) {
    return this.forcePromoteIlBookingMutation.mutate(
      { input: { ilBookingId } },
      { refetchQueries: ['LoadILClassDetails'] }
    );
  }

  createForUser(input: CreateIlBookingForUserInput) {
    return this.createIlBookingForUserMutation
      .mutate(
        { input },
        {
          update: (store, { data: { createIlBookingForUser } }) => {
            if (createIlBookingForUser.errors.length > 0) {
              return;
            }

            try {
              const id = gidToApolloCacheKey(createIlBookingForUser.ilBooking.ilClass.gid);
              store.modify({
                id,
                fields: {
                  ilBookings: append(createIlBookingForUser.ilBooking)
                }
              });
            } catch {
              console.warn('no cache to update');
            }
          }
        }
      )
      .pipe(
        map(({ data }) => data.createIlBookingForUser),
        finalMap('ilBooking')
      );
  }

  updateMultipleClassResults(input: UpdateIlClassResultsInput) {
    return this.updateMultipleIlClassResultsMutation.mutate({ input }).pipe(
      map(({ data }) => data.updateIlClassResults),
      finalMap('ilClassResults')
    );
  }

  createPendingReservation(input: CreatePendingIlBookingReservationInput) {
    return this.createPendingReservationMutation.mutate({ input }).pipe(
      map(({ data }) => data.createPendingIlBookingReservation),
      finalMap('pendingIlBookingReservation')
    );
  }
}
